import React from 'react';
import { Link } from 'react-router-dom';
import { Info, Shield, BookMarked } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

/* AdvertiseWithUs — dedicated media-pack page for direct advertisers and
   AdSense approval signals (original copy, real contact, policy link). */
export default function AdvertiseWithUs() {
    useSEO({
        title: 'Advertise with Linguistic Quest — family-safe ad placements',
        description: 'Reach UK and diaspora families learning heritage and world languages. Direct ad placements on Linguistic Quest are family-safe — no gambling, alcohol, dating or political advocacy. Email info@linguisticquest.com for a media pack.',
        path: '/advertise',
    });
    const slots = [
        {
            name: 'Home hero banner',
            location: 'Home page — below the hero, above the language library.',
            visibility: '100% of landing-page traffic.',
            format: 'Horizontal banner · 1200×200 (desktop) / 720×240 (mobile). JPG/PNG/WebP.',
            hyperlink: 'Single click-through URL (new tab, rel="sponsored").',
            good_fit: 'Children\'s books, museums, heritage-cultural organisations, educational toys, family travel.',
        },
        {
            name: 'Languages page banner',
            location: 'Languages page — above the language grid.',
            visibility: 'High-intent shoppers evaluating the product.',
            format: 'Horizontal banner · 1200×160 (desktop) / 720×200 (mobile). JPG/PNG/WebP.',
            hyperlink: 'Single click-through URL.',
            good_fit: 'Language schools, tutors, translation services, publishers.',
        },
        {
            name: 'Puzzle unlock — rewarded ad',
            location: 'Inside the pre-puzzle rewarded-ad modal for free-tier parents.',
            visibility: 'Highest-intent placement. Each unlock = 3 guaranteed impressions.',
            format: '10–15 second MP4 or GIF, 720p. Click-through optional.',
            hyperlink: 'Optional.',
            good_fit: 'Family-friendly brands with a short video or animated message.',
        },
    ];
    return (
        <main className="max-w-5xl mx-auto px-6 py-16" data-testid="page-advertise">
            <p className="text-sm font-semibold uppercase tracking-wider text-lq-purple-500">Advertise</p>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold text-lq-ink-black">Reach families learning heritage languages.</h1>
            <p className="mt-4 text-lg text-lq-ink-700 max-w-3xl leading-relaxed">
                Linguistic Quest is read by parents, grandparents, teachers and children on a mission to keep mother tongues
                alive. A small number of direct-advertising slots are available. Our editorial standard is <strong>family- and
                school-safe</strong>: no gambling, no alcohol, no adult categories, no dark patterns.
            </p>

            <section className="mt-10 space-y-4">
                {slots.map((s) => (
                    <div key={s.name} className="bg-white border border-lq-ink-100 rounded-2xl p-5" data-testid={`advertise-slot-${s.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                        <h2 className="font-display text-xl text-lq-ink-900">{s.name}</h2>
                        <dl className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
                            <Row label="Location" value={s.location} />
                            <Row label="Visibility" value={s.visibility} />
                            <Row label="Format" value={s.format} />
                            <Row label="Click-through" value={s.hyperlink} />
                            <Row label="Good fit" value={s.good_fit} />
                        </dl>
                    </div>
                ))}
            </section>

            <section className="mt-10 grid md:grid-cols-3 gap-4">
                <Panel icon={Info} title="How to book">
                    Email <a href="mailto:info@linguisticquest.com" className="text-lq-purple-700 hover:underline">info@linguisticquest.com</a>{' '}
                    with the slot name, your brand, creative (or link to it), and desired campaign dates. We reply within two working days.
                </Panel>
                <Panel icon={Shield} title="Editorial standards">
                    We reserve the right to decline any creative that does not meet our family- and school-safe standard. See our{' '}
                    <Link to="/legal/advertising" className="text-lq-purple-700 hover:underline">advertising policy</Link>.
                </Panel>
                <Panel icon={BookMarked} title="Programmatic ads">
                    Unsold direct-ad inventory falls back to Google AdSense with sensitive-category filters enabled. Paid subscribers
                    see no ads anywhere on the site.
                </Panel>
            </section>
        </main>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex gap-2"><dt className="text-xs uppercase tracking-wider text-lq-ink-500 w-24">{label}</dt><dd className="flex-1 text-lq-ink-700">{value}</dd></div>
    );
}
function Panel({ icon: Icon, title, children }) {
    return (
        <div className="bg-lq-paper border border-lq-ink-100 rounded-2xl p-5">
            <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-lq-purple-100 text-lq-purple-700 mb-2"><Icon size={16} /></div>
            <h3 className="font-display text-lg text-lq-ink-900">{title}</h3>
            <div className="mt-1 text-sm text-lq-ink-700 leading-relaxed">{children}</div>
        </div>
    );
}
